import { programsRequireCreditBundle } from "@outschool/funding-programs-shared";
import {
  UpdateCurrentUserDetailsMutation,
  UpdateCurrentUserDetailsMutationVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import {
  FullCurrentUserFragment,
  useEsaExperience,
  useFundingProgramAbbr,
  useSession,
} from "@outschool/ui-auth";
import React from "react";

import { ESA_CREDITS_TO_WALLET_FEATURE_FLAG } from "../../../../shared/featureFlags";
import { useFeatureFlag } from "../../../lib/FeatureFlags";

const updateCurrentUserDetailsMutation = gql`
  mutation UpdateCurrentUserDetails($details: UserDetailsInput) {
    updateCurrentUser(details: $details) {
      ...FullCurrentUserFragment
    }
  }
  ${FullCurrentUserFragment}
`;

/**
 * Whether listing/search prices should show credits vs dollars.
 * ESA wallet-dollar users see dollars (consistent with nav/checkout).
 * Subscription membership credits are unchanged and still use credit display.
 *
 * Note: `canPayWithSubscriptionCredits` is true for ESA membership users as well
 * as paid Outschool subscribers (server conflates the two). When the wallet flag
 * is on, ESA-only users must see dollars — only a real paid subscription keeps
 * credit pricing.
 */
export function computeShowCredits({
  requiresMembershipForEnrollment,
  requiresEsaMembershipForEnrollment,
  canPayWithSubscriptionCredits,
  isEsaWalletDollarsEnabled,
  isEsaUser,
  hasPaidSubscription,
}: {
  requiresMembershipForEnrollment: boolean;
  requiresEsaMembershipForEnrollment: boolean;
  canPayWithSubscriptionCredits?: boolean | null;
  isEsaWalletDollarsEnabled: boolean;
  isEsaUser: boolean;
  hasPaidSubscription?: boolean | null;
}): boolean {
  // canPayWithSubscriptionCredits is also true for ESA (server conflates).
  // When the wallet flag is on and there is no paid Outschool subscription,
  // treat that tender as ESA wallet dollars — even if isEsaUser detection
  // missed (e.g. funding-program cookie without experience context).
  const esaWalletShowsDollars =
    isEsaWalletDollarsEnabled &&
    !hasPaidSubscription &&
    (isEsaUser || !!canPayWithSubscriptionCredits);

  if (esaWalletShowsDollars) {
    return false;
  }

  return (
    requiresMembershipForEnrollment ||
    !!canPayWithSubscriptionCredits ||
    (requiresEsaMembershipForEnrollment && !isEsaWalletDollarsEnabled)
  );
}

export function useMembershipRequirement() {
  const { isLoggedIn, currentUser } = useSession();
  const { isEsaExperience } = useEsaExperience();
  const esaState = useFundingProgramAbbr();
  const isEsaWalletDollarsEnabled = useFeatureFlag(
    ESA_CREDITS_TO_WALLET_FEATURE_FLAG,
  );

  const [updateCurrentUserDetails] = useMutation<
    UpdateCurrentUserDetailsMutation,
    UpdateCurrentUserDetailsMutationVariables
  >(updateCurrentUserDetailsMutation);

  React.useEffect(() => {
    if (currentUser?.requiresMembershipForEnrollment === true) {
      updateCurrentUserDetails({
        variables: {
          details: {
            requiresMembershipForEnrollment: false,
          },
        },
      });
    }
  }, [currentUser?.requiresMembershipForEnrollment, updateCurrentUserDetails]);
  // membership is currently optional for all users
  const requiresMembershipForEnrollment = false;

  const requiresEsaMembershipForEnrollment =
    isLoggedIn &&
    isEsaExperience &&
    programsRequireCreditBundle.includes(esaState ?? "");

  const mustPurchaseMembership = false;

  const mustPurchaseEsaMembership =
    requiresEsaMembershipForEnrollment && !currentUser?.hasEsaMembership;

  const isEsaUser =
    isEsaExperience ||
    !!currentUser?.hasEsaMembership ||
    requiresEsaMembershipForEnrollment;

  const showCredits = computeShowCredits({
    requiresMembershipForEnrollment,
    requiresEsaMembershipForEnrollment,
    canPayWithSubscriptionCredits: currentUser?.canPayWithSubscriptionCredits,
    isEsaWalletDollarsEnabled,
    isEsaUser,
    hasPaidSubscription: currentUser?.hasSubscription,
  });

  return {
    // Logged in or logged out user is required to have a membership to enroll, regardless if they have a membership or not
    requiresMembershipForEnrollment,
    // User `requiresMembershipForEnrollment` + does NOT have a membership
    mustPurchaseMembership,

    // Logged in user is required to have an ESA membership to enroll, regardless if they have an ESA membership or not
    requiresEsaMembershipForEnrollment,
    // User `requiresEsaMembershipForEnrollment` + does NOT have a membership
    mustPurchaseEsaMembership,

    // User should see credits vs dollar amounts
    showCredits,
  };
}
